import { Col, Input, Row, Typography } from "antd";
import { useStoreIncomingPostbackMutation } from "api/TenantSlug/IncomingPostback/hooks.ts";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { AdvertiserSelectSearchable } from "components/UI/Selects/AdvertiserSelectSearchable/AdvertiserSelectSearchable.tsx";
import { ConversionTypeSelectSearchable } from "components/UI/Selects/ConversionTypeSelectSearchable/ConversionTypeSelectSearchable.tsx";
import { CountrySelectSearchable } from "components/UI/Selects/CountrySelectSearchable/CountrySelectSearchable.tsx";
import { OfferSelectSearchable } from "components/UI/Selects/OfferSelectSearchable/OfferSelectSearchable.tsx";
import { PartnerSelectSearchable } from "components/UI/Selects/PartnerSelectSearchable/PartnerSelectSearchable.tsx";
import { SourceSelectSearchable } from "components/UI/Selects/SourceSelectSearchable/SourceSelectSearchable.tsx";
import { StatusSelectSearchable } from "components/UI/Selects/StatusSelectSearchable/StatusSelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser";
import { MULTISELECT_ALL_VALUE, TOAST_TYPES } from "localConstants";
import { FC, useEffect, useRef } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { showCustomToast } from "utils/toast";

import { AddTrackingToolFromFormData, schema } from "./formSchema";

import StoreIncomingPostbackRequest = App.Http.Requests.Tracking.StoreIncomingPostbackRequest;

interface AddTrackingToolFromFormProps {
    onSubmit?: () => void;
    formId?: string;
}

const hasSameValues = <T,>(current: T[], next: T[]) =>
    current.length === next.length && current.every((value, index) => value === next[index]);

const AddTrackingToolFromFormContent: FC<AddTrackingToolFromFormProps> = () => {
    const { t } = usePaldockTranslation("forms.add-tracking-tool-from");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tC } = usePaldockTranslation("common");
    const generalFormUtils = useGeneralForm<AddTrackingToolFromFormData>();
    const { control, fieldNames, watch, setValue } = generalFormUtils;

    const includedOffers = watch("offer_ids") ?? [];
    const excludedOffers = watch("excluded_offer_ids") ?? [];
    const includedIsAll = includedOffers.includes(MULTISELECT_ALL_VALUE);
    const excludedIsAll = excludedOffers.includes(MULTISELECT_ALL_VALUE);
    const includedHasSpecific = includedOffers.length > 0 && !includedIsAll;
    const excludedHasSpecific = excludedOffers.length > 0 && !excludedIsAll;
    const previousIncludedIsAll = useRef(includedIsAll);
    const previousExcludedIsAll = useRef(excludedIsAll);

    useEffect(() => {
        const wasIncludedAll = previousIncludedIsAll.current;
        const wasExcludedAll = previousExcludedIsAll.current;

        if (!includedOffers.length && !excludedOffers.length) {
            const nextIncludedOffers =
                wasExcludedAll && !wasIncludedAll ? [MULTISELECT_ALL_VALUE] : ([] as typeof includedOffers);
            const nextExcludedOffers =
                wasIncludedAll && !wasExcludedAll ? [MULTISELECT_ALL_VALUE] : ([] as typeof excludedOffers);

            if (!hasSameValues(includedOffers, nextIncludedOffers)) {
                setValue("offer_ids", nextIncludedOffers);
            }

            if (!hasSameValues(excludedOffers, nextExcludedOffers)) {
                setValue("excluded_offer_ids", nextExcludedOffers);
            }
        } else if (includedIsAll && excludedIsAll) {
            if (!wasIncludedAll && wasExcludedAll && excludedOffers.length > 0) {
                setValue("excluded_offer_ids", []);
            } else if (!wasExcludedAll && wasIncludedAll && includedOffers.length > 0) {
                setValue("offer_ids", []);
            }
        }

        previousIncludedIsAll.current = includedIsAll;
        previousExcludedIsAll.current = excludedIsAll;
    }, [excludedIsAll, excludedOffers, includedIsAll, includedOffers, setValue]);

    return (
        <div className={"bg-gray-100 rounded-lg border-dashed p-1 "}>
            <div className="p-4 bg-white rounded-lg m-3">
                <Typography.Title level={4}>{tC("settings")}</Typography.Title>
                <Row gutter={16}>
                    <Col span={12}>
                        <FormItem control={control} name={fieldNames.name.name} label={tFields("name.label")} required>
                            <Input placeholder={tFields("name.placeholder")} />
                        </FormItem>
                    </Col>
                    <Col span={12}>
                        <FormItem
                            control={control}
                            name={fieldNames.is_active?.name || "is_active"}
                            label={tFields("state.label")}
                        >
                            <StatusSelectSearchable />
                        </FormItem>
                    </Col>
                </Row>
            </div>

            <div className="p-4 bg-white rounded-lg m-3">
                <Typography.Title level={4}>{t("sections.conditions")}</Typography.Title>
                <Row gutter={16}>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.sources?.name || "sources"}
                            label={tFields("source.label")}
                        >
                            <SourceSelectSearchable mode="multiple" allowClear />
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.types?.name || "types"}
                            label={tFields("conversion_type.label")}
                        >
                            <ConversionTypeSelectSearchable mode="multiple" allowClear />
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.affiliate_ids?.name || "affiliate_ids"}
                            label={tFields("affiliates.label")}
                        >
                            <PartnerSelectSearchable mode="multiple" withAllOption />
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.advertiser_ids?.name || "advertiser_ids"}
                            label={tFields("advertisers.label")}
                        >
                            <AdvertiserSelectSearchable mode="multiple" withAllOption />
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.country_id?.name || "country_id"}
                            label={tFields("country.label")}
                        >
                            <CountrySelectSearchable allowClear />
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <FormItem
                            control={control}
                            name={fieldNames.offer_ids?.name || "offer_ids"}
                            label={tFields("included_offers.label")}
                        >
                            <OfferSelectSearchable mode="multiple" withAllOption disabled={excludedHasSpecific} />
                        </FormItem>
                    </Col>
                    <Col span={12}>
                        <FormItem
                            control={control}
                            name={fieldNames.excluded_offer_ids?.name || "excluded_offer_ids"}
                            label={tFields("excluded_offers.label")}
                        >
                            <OfferSelectSearchable mode="multiple" withAllOption disabled={includedHasSpecific} />
                        </FormItem>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export const AddTrackingToolFromForm: FC<AddTrackingToolFromFormProps> = ({ onSubmit, formId }) => {
    const { t } = usePaldockTranslation("forms.add-tracking-tool-from");
    const { t: tC } = usePaldockTranslation("common");
    const generalFormUtils = useGeneralForm<AddTrackingToolFromFormData>();
    const { handleErrorsFromErrorResponse } = generalFormUtils;

    const storeRequestMutation = useStoreIncomingPostbackMutation();
    const userUtils = useUser();

    const defaultValues: AddTrackingToolFromFormData = {
        name: "",
        is_active: false,
        first_sequence_step_id: null,
        affiliate_ids: [MULTISELECT_ALL_VALUE],
        advertiser_ids: [MULTISELECT_ALL_VALUE],
        offer_ids: [MULTISELECT_ALL_VALUE],
        excluded_offer_ids: [],
    };

    const handleMultiSelectValue = <T extends string | number>(
        arr: (T | typeof MULTISELECT_ALL_VALUE)[] = []
    ): Record<string, T> => {
        if (arr.includes(MULTISELECT_ALL_VALUE)) return {};
        return Object.fromEntries(arr.map((val) => [val, val])) as Record<string, T>;
    };

    const handleMultiSelectArray = (arr: (string | typeof MULTISELECT_ALL_VALUE)[] = []): Array<string> => {
        if (arr.includes(MULTISELECT_ALL_VALUE)) return [];
        return arr as string[];
    };

    const onSubmitForm = (data: AddTrackingToolFromFormData) => {
        const payload = {
            name: data.name,
            is_active: data.is_active,
            first_sequence_step_id: data.first_sequence_step_id,
            types: handleMultiSelectValue(data.types),
            sources: handleMultiSelectValue(data.sources),
            offer_ids: handleMultiSelectArray(data.offer_ids),
            excluded_offer_ids: handleMultiSelectArray(data.excluded_offer_ids),
            country_id: data.country_id ?? null,
            affiliate_ids: handleMultiSelectArray(data.affiliate_ids),
            advertiser_ids: handleMultiSelectArray(data.advertiser_ids),
        } as StoreIncomingPostbackRequest;

        storeRequestMutation.mutate(payload, {
            onSuccess: (response) => {
                showCustomToast(
                    {
                        title: t("messages.success"),
                        buttons: [
                            {
                                label: tC("open"),
                                href: response?.incomingPostback?.id
                                    ? `/${userUtils.getActiveTenant()?.slug_name}/tool/tracking/s2s-postback/${response.incomingPostback.id}`
                                    : undefined,
                            },
                        ],
                    },
                    TOAST_TYPES.SUCCESS
                );
                onSubmit?.();
            },
            onError: (error) => {
                toast.error(`${t("messages.error")}: ${error.message}`);
                handleErrorsFromErrorResponse(error);
            },
        });
    };

    return (
        <div>
            <GeneralFormProvider<AddTrackingToolFromFormData>
                id={formId}
                schema={schema}
                defaultValues={defaultValues}
                onSubmit={onSubmitForm}
            >
                <AddTrackingToolFromFormContent />
            </GeneralFormProvider>
        </div>
    );
};
